import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthSurface, Button, FormField, Input, Alert } from '@sakhtar/design-system/react';
import logoUrl from '@sakhtar/design-system/assets/logo/sakhtar-white-logo.svg';
import { operatorLogin, OperatorAuthError } from '../../lib/operatorApi.js';

/**
 * UI_REFERENCE_SOURCE:
 * section: (no dedicated board — Cloud ERP's own cross-tenant operator
 *   console; see PRODUCTION_READINESS_AUDIT.md's "Pilot Admin Console"
 *   section. `Sakhtar Pilot P0.html`'s pre-auth screens — Login/OTP/Session
 *   Expired — are the different, already-cited tenant-scoped
 *   `/t/:slug/login` flow, not this operator console.)
 * canonical pattern: `AuthSurface` + `FormField`/`Input`/`Button`, the same
 *   v1 auth-shell primitive the tenant pre-auth pages use.
 * state reference: idle · submitting · error
 *
 * Single required field (`رمز اپراتور`), matching `operatorLogin`'s only
 * parameter (`operatorApi.ts:75`, `POST .../session/login { password }`) —
 * no username field because there is none server-side; the operator
 * credential is a shared password, not a per-operator account.
 */
export function OperatorLoginPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await operatorLogin(password);
      navigate('/internal/admin/organizations');
    } catch (err) {
      setError(
        err instanceof OperatorAuthError
          ? 'ورود اپراتور ناموفق بود.'
          : 'ارتباط با سرور برقرار نشد.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthSurface
      logo={<img src={logoUrl} alt="ساختار سیستم" height={32} />}
      brandLabel="ساختار سیستم"
      title="ورود اپراتور"
      subtitle="کنسول داخلی — جدا از فضای مشتری"
    >
      <form onSubmit={onSubmit} noValidate>
        <FormField label="رمز اپراتور" required>
          <Input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </FormField>
        {error ? (
          <Alert tone="danger" title="ورود ناموفق">
            {error}
          </Alert>
        ) : null}
        <Button type="submit" loading={submitting} style={{ width: '100%' }}>
          ورود به کنسول
        </Button>
      </form>
    </AuthSurface>
  );
}
